import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AlertTriangle, HelpCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PopupContext = createContext(null);

const EMPTY_STATE = { open: false, type: "confirm", options: {}, resolve: null, value: "" };

export function PopupProvider({ children }) {
  const [state, setState] = useState(EMPTY_STATE);

  const close = useCallback((result) => {
    setState((current) => {
      current.resolve?.(result);
      return EMPTY_STATE;
    });
  }, []);

  const confirm = useCallback((options) => new Promise((resolve) => {
    setState({ open: true, type: "confirm", options: options || {}, resolve, value: "" });
  }), []);

  const prompt = useCallback((options) => new Promise((resolve) => {
    setState({
      open: true,
      type: "prompt",
      options: options || {},
      resolve,
      value: options?.defaultValue || "",
    });
  }), []);

  const api = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);
  const isPrompt = state.type === "prompt";
  const tone = state.options?.tone || (isPrompt ? "indigo" : "danger");
  const isDanger = tone === "danger";
  const Icon = isDanger ? AlertTriangle : HelpCircle;
  const value = state.value || "";

  function submitPrompt() {
    const trimmed = state.options?.trim === false ? value : value.trim();
    if (state.options?.required && !trimmed) return;
    close(trimmed);
  }

  return (
    <PopupContext.Provider value={api}>
      {children}
      <Dialog open={state.open} onOpenChange={(open) => { if (!open) close(isPrompt ? null : false); }}>
        <DialogContent className="max-w-md rounded-xl border-slate-200 p-0 shadow-2xl">
          <div className="p-6">
            <DialogHeader>
              <div className={`mb-3 flex h-11 w-11 items-center justify-center rounded-xl ${isDanger ? "bg-rose-50 text-rose-600" : "bg-blue-50 text-blue-600"}`}>
                <Icon size={22} />
              </div>
              <DialogTitle>{state.options?.title || (isPrompt ? "Enter details" : "Are you sure?")}</DialogTitle>
              {state.options?.description && (
                <DialogDescription className="leading-6">{state.options.description}</DialogDescription>
              )}
            </DialogHeader>

            {isPrompt && (
              <div className="mt-5 space-y-1.5">
                <Label htmlFor="app-popup-input">{state.options?.label || "Value"}</Label>
                <Input
                  id="app-popup-input"
                  autoFocus
                  type={state.options?.inputType || "text"}
                  inputMode={state.options?.inputMode}
                  placeholder={state.options?.placeholder}
                  value={value}
                  onChange={(e) => setState((current) => ({ ...current, value: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitPrompt();
                    }
                  }}
                />
                {state.options?.helperText && <p className="text-xs text-slate-500">{state.options.helperText}</p>}
              </div>
            )}
          </div>

          <DialogFooter className="border-t border-slate-100 bg-slate-50 px-6 py-4">
            <Button variant="outline" onClick={() => close(isPrompt ? null : false)}>
              {state.options?.cancelText || "Cancel"}
            </Button>
            <Button
              className={isDanger ? "bg-rose-600 hover:bg-rose-700" : "bg-blue-600 hover:bg-blue-700"}
              onClick={isPrompt ? submitPrompt : () => close(true)}
              disabled={isPrompt && state.options?.required && !value.trim()}
            >
              {state.options?.confirmText || (isPrompt ? "Save" : "Confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PopupContext.Provider>
  );
}

export function usePopup() {
  const context = useContext(PopupContext);
  if (!context) throw new Error("usePopup must be used inside PopupProvider");
  return context;
}
